import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Target, CheckCircle2, Sparkles, Clock, ArrowRight, PartyPopper } from "lucide-react";

const TONE_STYLES = {
  gentle:       { ring: "border-primary/30",  glow: "from-primary/10",  label: "gentle mission" },
  encouraging:  { ring: "border-accent/40",   glow: "from-accent/10",   label: "today's mission" },
  celebratory:  { ring: "border-primary/60",  glow: "from-primary/20",  label: "protect the streak" },
};

export default function DailyMissionCard() {
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    api.get("/me/daily-mission")
      .then((r) => setMission(r.data))
      .catch(() => setMission(null))
      .finally(() => setLoading(false));
  }, []);

  const complete = async () => {
    if (!mission || mission.completed) return;
    setCompleting(true);
    try {
      const { data } = await api.post("/me/daily-mission/complete");
      setMission(data);
      toast.success("Mission accomplished");
    } catch { toast.error("Could not mark complete"); }
    finally { setCompleting(false); }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 animate-pulse">
        <div className="h-4 w-24 rounded bg-secondary" />
        <div className="mt-3 h-6 w-3/4 rounded bg-secondary" />
        <div className="mt-2 h-4 w-2/3 rounded bg-secondary" />
      </div>
    );
  }
  if (!mission) return null;

  const style = TONE_STYLES[mission.tone] || TONE_STYLES.encouraging;
  const isDone = !!mission.completed;

  return (
    <div
      data-testid="dashboard-daily-mission"
      className={`relative overflow-hidden rounded-2xl border ${isDone ? "border-accent/60" : style.ring} bg-gradient-to-br ${isDone ? "from-accent/20" : style.glow} to-transparent p-6`}
    >
      <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/10 blur-2xl pointer-events-none" />
      <div className="relative flex items-start gap-3">
        <div className={`grid h-10 w-10 flex-shrink-0 place-items-center rounded-2xl border ${isDone ? "border-accent/60 bg-accent/20 text-accent" : "border-primary/40 bg-primary/10 text-primary"}`}>
          {isDone ? <PartyPopper className="h-5 w-5" /> : <Target className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`label-mono ${isDone ? "text-accent" : "text-primary"}`}>
            {isDone ? "// mission accomplished" : `// ${style.label}`}
          </div>
          <h2 className="mt-2 font-display text-xl font-black tracking-tight sm:text-2xl leading-tight">
            {isDone ? mission.completion_message : mission.mission}
          </h2>
          {!isDone && mission.rationale && (
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{mission.rationale}</p>
          )}
          {mission.motivation && (
            <div className={`mt-3 rounded-lg border ${isDone ? "border-accent/30 bg-accent/5" : "border-primary/20 bg-primary/5"} p-3`}>
              <div className="label-mono flex items-center gap-1"><Sparkles className="h-3 w-3" /> from your mentor</div>
              <p className="mt-1 text-sm leading-relaxed">{mission.motivation}</p>
            </div>
          )}

          {!isDone && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {mission.next_lesson?.url && (
                <Button data-testid="mission-start-btn" asChild className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest">
                  <Link to={mission.next_lesson.url}>
                    Start · {mission.next_lesson.title.slice(0, 32)}{mission.next_lesson.title.length > 32 ? "…" : ""} <ArrowRight className="ml-2 h-3.5 w-3.5" />
                  </Link>
                </Button>
              )}
              <Button
                data-testid="mission-complete-btn"
                onClick={complete}
                disabled={completing}
                variant="outline"
                className="rounded-full font-mono text-xs uppercase tracking-widest"
              >
                <CheckCircle2 className="mr-2 h-3.5 w-3.5" /> {completing ? "…" : "Mark done"}
              </Button>
              <div className="ml-auto label-mono inline-flex items-center gap-1"><Clock className="h-3 w-3" /> ~{mission.duration_min} min</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
